import { Box, Button, Grid, IconButton, TextField, Typography, Link as MuiLink, Tooltip, Chip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { DataGrid } from '@mui/x-data-grid'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ClientRoute } from 'routes/client'
import { selectLeaderboard } from 'store/leaderboard/slice'
import { getChallengeLeaderboard } from 'store/leaderboard/thunks'
import { resetParticipantRounds } from 'store/participantRounds/slice'
import { removeUserFromChallenge } from 'store/userChallenge/thunks'
import DeleteChallengeDialog from '../userChallenge/DeleteChallengeDialog'
import RemovePlayerDialog from '../userChallenge/RemovePlayerDialog'
import ParticipantRoundsCell from '../userChallenge/ParticipantRoundsCell'
import { useAppDispatch } from 'store'
import { ChevronLeftIcon } from 'assets/icons/chevron-left'
import { useIntl } from 'react-intl'
import { sendErrorNotification, sendSuccessNotification } from 'utils/notifications'
import {
  ChallengeComputedState,
  ChallengeQuickInsightCalculation,
  getSponsoredChallengePointType,
  getSponsoredChallengeTypeName,
} from 'utils/challenges'
import { ChallengeCategory, ChallengeTypeConfig } from 'api/types'
import { selectChallenge } from 'store/challenge/slice'
import { ChallengeImageType } from 'api/types'
import { getChallengeMedia } from 'store/challengesMedia/thunks'
import {
  addChallengePlayer,
  deleteChallenge,
  getChallenge,
  getChallengeLeaderboardReportV1,
} from 'store/challenge/thunks'
import { EditPencilIcon } from 'assets/icons/edit-pencil-icon'
import type { GetChallengeInsightsResponse } from 'api/types'
import { getChallengeInsights, recalculateChallenge } from 'store/leaderboards/thunks'
import { RecalculateIcon } from 'assets/icons/recalculate-icon'
import { ExportIcon } from 'assets/icons/export-icon'
import { DeleteIcon } from 'assets/icons/deleteIcon'
import { RemoveIcon } from 'assets/icons/remove'
import { Launch } from '@mui/icons-material'
import StatusBadge from './StatusBadge'

const mixpanelUrl =
  // eslint-disable-next-line max-len
  'https://mixpanel.com/project/529239/view/27300/app/boards#id=11493010&filters=%7E%28%7E%28resourceType%7E%27event%7EpropertyName%7E%27Environment%7EpropertyObjectKey%7Enull%7EpropertyDefaultType%7E%27string%7EpropertyType%7E%27string%7EfilterOperator%7E%27does*20not*20equal%7EfilterValue%7E%28%7E%27RC%7E%27Sandbox%7E%27staging%7E%27Staging%29%7ElimitValues%7Efalse%7EdefaultEmpty%7Efalse%7EactiveValue%7E%28%7E%27RC%7E%27Sandbox%7E%27staging%7E%27Staging%29%29%7E%28resourceType%7E%27user%7EpropertyName%7E%27Environment%7EpropertyObjectKey%7Enull%7EpropertyDefaultType%7E%27string%7EpropertyType%7E%27string%7EfilterOperator%7E%27does*20not*20equal%7EfilterValue%7E%28%7E%27core.rc%7E%27core.sandbox%7E%27core.staging%7E%27Local%7E%27RC%7E%27Sandbox%7E%27Staging%29%7ElimitValues%7Efalse%7EdefaultEmpty%7Efalse%7EactiveValue%7E%28%7E%27core.rc%7E%27core.sandbox%7E%27core.staging%7E%27Local%7E%27RC%7E%27Sandbox%7E%27Staging%29%29%7E%28resourceType%7E%27user%7EpropertyName%7E%27is_developer%7EpropertyObjectKey%7Enull%7EpropertyDefaultType%7E%27boolean%7EpropertyType%7E%27boolean%7EfilterOperator%7E%27false%7EfilterValue%7Efalse%7ElimitValues%7Efalse%7EdefaultEmpty%7Efalse%29%7E%28resourceType%7E%27event%7EpropertyName%7E%27challengeId%7EpropertyObjectKey%7Enull%7EpropertyDefaultType%7E%27string%7EpropertyType%7E%27string%7EfilterOperator%7E%27equals%7EfilterValue%7E%28%7E%279946%29%7ElimitValues%7Efalse%7EdefaultEmpty%7Efalse%7EactiveValue%7E%28%7E%279946%29%29%29'

const useStyles = makeStyles(() => ({
  deleteChallenge: {
    color: 'white',
    width: '28px',
    backgroundColor: '#E63946',
  },
  challengeDetails: {
    color: '#D9D9D9',
    fontSize: 16,
    fontWeight: '500',
  },
  textInput: {
    border: '1px solid #D9D9D9',
    borderRadius: '34px',
    padding: '8px 12px',
    lineHeight: '24px',
    minWidth: '240px',
    fontSize: '16px',
  },
  challengeHeader: {
    display: 'flex',
    width: '100%',
    backgroundColor: '#32724C',
    borderRadius: '16px',
    padding: '16px 40px',
  },
  challengeInfoContainer: {
    display: 'flex',
    width: '100%',
    fontFamily: 'Söhne',
  },
  challengeInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    alignContent: 'flex-start',
    padding: 20,
  },
  challengeImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  challengeImage: {
    width: '115px',
    height: '115px',
    borderRadius: '50%',
    border: '3px solid #fff',
    backgroundColor: '#fff',
  },
  challengeName: {
    fontSize: '30px',
    fontWeight: 600,
    color: '#fff',
    flexBasis: '100%',
  },
  button: {
    borderRadius: '8px',
    height: '42px',
    minWidth: '150px',
  },
  evenRow: {
    backgroundColor: '#F5F5F9',
  },
  chip: {
    fontSize: '16px',
    fontWeight: 500,
    background: '#F5F5F9',
    padding: '8px 12px',
    maxWidth: '100%',
    height: 'auto',
    whiteSpace: 'normal',
    borderRadius: '999px',
  },
}))

const ChallengeLeaderboard = () => {
  const dispatch = useAppDispatch()
  const classes = useStyles()
  const { id: challengeId } = useParams()
  const navigate = useNavigate()
  const { leaderboard, status: leaderboardStatus } = useSelector(selectLeaderboard)
  const { challenge } = useSelector(selectChallenge)
  const [initialLeaderboardFetched, setInitialLeaderboardFetched] = useState(false)
  const [initialChallengeFetched, setInitialChallengeFetched] = useState(false)
  const [showDeleteChallengeDialog, setShowDeleteChallengeDialog] = useState(false)
  const [showRemovePlayerDialog, setShowRemovePlayerDialog] = useState(false)
  const [playerToRemove, setPlayerToRemove] = useState<string | null>(null)
  const [participantForRounds, setParticipantForRounds] = useState<{
    participantId: string
    name: string
    userId?: number
  } | null>(null)
  const [searchText, setSearchText] = useState('')
  const [userIdInput, setUserIdInput] = useState('')
  const intl = useIntl()
  const location = useLocation()
  const [challengeLogo, setChallengeLogo] = useState<string | undefined>()

  const [quickInsights, setQuickInsights] = useState<GetChallengeInsightsResponse['data'] | undefined>()

  useEffect(() => {
    const loadQuickInsights = async () => {
      if (!challenge.challengeId || challenge.challengeId === '-1') {
        return
      }

      const result = await dispatch(getChallengeInsights(challenge.challengeId)).unwrap()

      setQuickInsights(result.data)
    }

    loadQuickInsights()
  }, [challenge.challengeId])

  useEffect(() => {
    if (challengeId && !initialLeaderboardFetched) {
      dispatch(getChallengeLeaderboard({ challengeId, params: { limit: 100 } }))
      setInitialLeaderboardFetched(true)
    }
  }, [challengeId, initialLeaderboardFetched])

  useEffect(() => {
    return () => {
      dispatch(resetParticipantRounds())
    }
  }, [])

  useEffect(() => {
    if (challengeId && !initialChallengeFetched) {
      dispatch(getChallenge(challengeId))
      setInitialChallengeFetched(true)
    }
  }, [challengeId, initialChallengeFetched])

  const columns = [
    {
      field: 'rank',
      headerName: 'Rank',
      flex: 1,
      renderCell: (params) => (params.row.rank !== '-' ? <span>{params.row.rank}.</span> : '-'),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            wordBreak: 'break-word',
            maxWidth: '100%',
            lineHeight: '18px',
          }}
        >
          <Link
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
            to={ClientRoute.ADMIN_USER(params.row.id || 0)}
          >
            {params.row.name}
          </Link>
        </span>
      ),
    },
    {
      field: 'hcp',
      flex: 1,
      renderHeader: () => (
        <Tooltip
          title="Players current handicap"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#222A35',
                color: '#FFF',
                fontSize: '16px',
                padding: '20px',
                fontWeight: '400',
              },
            },
            arrow: {
              sx: {
                color: '#222A35',
              },
            },
          }}
        >
          <span style={{ fontWeight: 500 }}>HCP</span>
        </Tooltip>
      ),
      renderCell: (params) => <span>{params.row.hcp}</span>,
    },
    { field: 'country', headerName: 'Country', flex: 1 },
    {
      field: 'score',
      headerName: getSponsoredChallengeTypeName(challenge?.typeConfig, intl),
      flex: 1,
    },
    {
      field: 'rounds',
      headerName: 'Rounds',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <ParticipantRoundsCell
          participantId={params.row.participantId}
          onShowRounds={(participantId) =>
            setParticipantForRounds({ participantId, name: params.row.name, userId: params.row.id })
          }
        />
      ),
    },
    {
      field: 'removeUser',
      headerName: '',
      width: 100,
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => handleRemovePlayer(params.row.participantId)}
            sx={{ color: '#E63946' }}
          >
            <RemoveIcon />
          </IconButton>
        )
      },
    },
  ]

  const getParticipantHandicap = (handicap: number | undefined) => {
    if (typeof handicap !== 'number') {
      return '0.0'
    }
    if (handicap <= 0) {
      return Math.abs(handicap).toString()
    }

    return `+${handicap}`
  }

  const rows = useMemo(() => {
    if (!leaderboard.length) {
      return []
    }

    return leaderboard.map((entry) => ({
      participantId: entry.participantId,
      id: entry.user?.userId,
      rank: entry.rank ?? '-',
      name: `${entry.user?.firstName ?? ''} ${entry.user?.lastName ?? ''}`,
      hcp: getParticipantHandicap(entry.user?.hcp),
      country: entry.user?.country ?? '',
      score: entry.score,
      removeUser: '',
    }))
  }, [leaderboard])

  const filteredRows = useMemo(() => {
    if (!searchText.trim() || searchText.trim().length < 3) {
      return rows
    }
    const searchLower = searchText.toLowerCase()
    return rows.filter((row) => row.name.toLowerCase().includes(searchLower))
  }, [rows, searchText])

  const handleRemovePlayer = (participantId: string | null) => {
    setPlayerToRemove(participantId)
    setShowRemovePlayerDialog(true)
  }

  const handleConfirmRemovePlayer = () => {
    if (challenge.challengeId && playerToRemove) {
      dispatch(
        removeUserFromChallenge({
          challengeId: challenge.challengeId,
          participantId: playerToRemove,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(getChallengeLeaderboard({ challengeId: String(challenge.challengeId), params: { limit: 100 } }))
          setShowRemovePlayerDialog(false)
          setPlayerToRemove(null)
          sendSuccessNotification('User removed')
        })
        .catch((error) => {
          console.error(error)
          sendErrorNotification('Failed to remove user')
          setPlayerToRemove(null)
          setShowRemovePlayerDialog(false)
        })
    }
  }

  const handleShowSettings = () => navigate(ClientRoute.ADMIN_CHALLENGE_EDIT(String(challenge.challengeId)))

  const handleShowDeleteChallengeDialog = () => setShowDeleteChallengeDialog(true)

  const getTypeLogo = (userChallenge: Challenge) => {
    switch (userChallenge?.typeConfig?.type) {
      case ChallengeTypeConfig.BIRDIES_OR_BETTER:
        return '/images/userChallenges/icon_challenge_birdie.png'
      case ChallengeTypeConfig.NUMBER_OF_COURSES_PLAYED:
        return '/images/userChallenges/icon_challenge_course.png'
      case ChallengeTypeConfig.NUMBER_OF_HOLES_PLAYED:
        return '/images/userChallenges/icon_challenge_hole.png'
      case ChallengeTypeConfig.NUMBER_OF_PARS:
        return '/images/userChallenges/icon_challenge_par.png'
      case ChallengeTypeConfig.STABLEFORD:
        return '/images/userChallenges/icon_challenge_stableford.png'
      case ChallengeTypeConfig.STROKEPLAY:
        return '/images/userChallenges/icon_challenge_strokeplay.png'
      default:
        return ''
    }
  }

  useEffect(() => {
    if (!challenge?.challengeId || challenge.challengeId === '-1' || !challenge.media?.hasLogoImage) {
      return
    }
    if (challengeLogo) {
      return
    }
    const loadLogo = async () => {
      const challengeLogo = await dispatch(
        getChallengeMedia({
          challengeId: String(challenge.challengeId),
          challengeImageType: ChallengeImageType.LOGO,
        }),
      ).unwrap()
      setChallengeLogo(challengeLogo.data?.imageUrl)
    }
    loadLogo()
  }, [challenge])

  const hasCustomPerformanceScope = () =>
    (challenge.roundEligibility?.countries?.length ?? 0) > 0 || (challenge.roundEligibility?.clubIds?.length ?? 0) > 0

  const hasCustomVisibilityScope = () =>
    Boolean(
      !challenge.audienceControl?.isDiscoverable ||
        (challenge.audienceControl?.joinAudience?.countries?.length ?? 0) > 0 ||
        (challenge.audienceControl?.joinAudience?.clubIds?.length ?? 0) > 0,
    )

  const handleShowParticipantRounds = () => {
    if (!participantForRounds || !participantForRounds.userId) {
      return
    }

    navigate(ClientRoute.ADMIN_USER(participantForRounds.userId, 'challenges'), {
      state: {
        userId: participantForRounds.userId,
        participantId: participantForRounds.participantId,
        participantName: participantForRounds.name,
        from: location.pathname,
        challengeId: challenge.challengeId,
      },
    })
  }

  const getQuickInsightChips = (quickInsights: GetChallengeInsightsResponse['data']) => {
    if (!challenge.challengeId || challenge.challengeId === '-1') {
      return
    }

    if (participantForRounds) {
      handleShowParticipantRounds()
      return null
    }

    return (
      <Grid item sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'flex-start',
            alignItems: 'center',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <Chip className={classes.chip} label={`Participants: ${quickInsights?.participantCount ?? 0}`} />
          <Chip className={classes.chip} label={`Total rounds played: ${quickInsights?.totalRoundsPlayed ?? 0}`} />
          {quickInsights?.metric?.type !== ChallengeTypeConfig.ECLECTIC && (
            <>
              <Chip
                className={classes.chip}
                label={`Total [${getSponsoredChallengePointType(
                  quickInsights?.metric?.type ?? '',
                  intl,
                )}]: ${quickInsights?.metric?.total}`}
              />
              <Tooltip
                // eslint-disable-next-line max-len
                title={`Calculated by dividing the total number of [${ChallengeQuickInsightCalculation[quickInsights?.metric?.type ?? 'CHALLENGE_TYPE_UNSPECIFIED']}] by the number of players with more than 0 [${ChallengeQuickInsightCalculation[quickInsights?.metric?.type ?? 'CHALLENGE_TYPE_UNSPECIFIED']}].`}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#222A35',
                      color: '#FFF',
                      fontSize: '16px',
                      padding: '20px',
                      fontWeight: '400',
                    },
                  },
                  arrow: {
                    sx: {
                      color: '#222A35',
                    },
                  },
                }}
              >
                <Chip
                  className={classes.chip}
                  label={`Average [${getSponsoredChallengePointType(
                    quickInsights?.metric?.type ?? '',
                    intl,
                  )}] per player: ${quickInsights?.metric?.averagePerParticipant?.toFixed(1)}`}
                />
              </Tooltip>

              {quickInsights?.metric?.type !== ChallengeTypeConfig.NUMBER_OF_COURSES_PLAYED &&
                quickInsights?.metric?.type !== ChallengeTypeConfig.STROKEPLAY && (
                  <Chip
                    className={classes.chip}
                    label={`Average [${getSponsoredChallengePointType(
                      quickInsights?.metric?.type ?? '',
                      intl,
                    )}] per round: ${quickInsights?.metric?.averagePerRound?.toFixed(1)}`}
                  />
                )}
            </>
          )}
        </Box>
      </Grid>
    )
  }

  const handleRecalculate = () => {
    if (!challenge.challengeId || challenge.challengeId === '-1') {
      return
    }
    dispatch(recalculateChallenge(challenge.challengeId))
  }

  const handleExportResults = () => {
    if (!challenge.challengeId || challenge.challengeId === '-1') {
      return
    }
    dispatch(getChallengeLeaderboardReportV1(challenge.challengeId))
  }

  const handleDeleteChallenge = () => {
    if (!challenge.challengeId || challenge.challengeId === '-1') {
      return
    }
    dispatch(deleteChallenge(challenge.challengeId))
      .unwrap()
      .then(() => {
        setShowDeleteChallengeDialog(false)
        sendSuccessNotification('Challenge Deleted')
        navigate(ClientRoute.ADMIN_CHALLENGES)
      })
      .catch((error) => {
        console.error('Error deleting the challenge:', error)
        sendErrorNotification('failed to delete challenge')
        setShowDeleteChallengeDialog(false)
      })
  }

  const handleAddPlayer = () => {
    if (challenge.challengeId && userIdInput) {
      dispatch(
        addChallengePlayer({
          challengeId: challenge.challengeId,
          playerId: userIdInput,
          limit: 50,
          category: ChallengeCategory.SPONSORED,
        }),
      )
      setUserIdInput('')
    }
  }

  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => (event.currentTarget as HTMLInputElement).blur()

  return (
    <>
      <Grid container sx={{ padding: '8px', rowGap: 2, columnGap: 3 }}>
        {location.state?.from && (
          <Box gap={24}>
            <MuiLink
              style={{
                color: '#0BB163',
                gap: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() =>
                navigate(location.state.from, {
                  state: { selectedChallengeType: location.state.type },
                })
              }
            >
              <ChevronLeftIcon width="20px" height="20px" circleFill="#0BB163" pathStroke="#EDEFF1" />
              {intl.formatMessage({ id: 'strings.backToChallenges' })}
            </MuiLink>
          </Box>
        )}
        <div className={classes.challengeHeader}>
          <div className={classes.challengeInfoContainer} style={{ gap: '8px' }}>
            <div className={classes.challengeImageContainer}>
              <img
                className={classes.challengeImage}
                src={challenge.media?.hasLogoImage ? challengeLogo : getTypeLogo(challenge)}
                alt="Challenge"
              />
            </div>
            <div className={classes.challengeInfo}>
              <Typography className={classes.challengeName} component="div">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    maxWidth: '100%',
                    verticalAlign: 'middle',
                  }}
                >
                  {challenge?.info?.name}

                  <span
                    style={{
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <StatusBadge challengeState={challenge?.computedState} />
                  </span>
                </span>
              </Typography>
              <div>
                <Typography
                  className={classes.challengeDetails}
                >{`Type: ${getSponsoredChallengeTypeName(challenge?.typeConfig, intl)}`}</Typography>
                {challenge.typeConfig?.target && (
                  <Typography
                    className={classes.challengeDetails}
                  >{`Challenge target: ${challenge?.typeConfig?.target ?? '-'}`}</Typography>
                )}
                <Typography className={classes.challengeDetails}>
                  {intl.formatMessage({ id: 'strings.whereCanBePerformed' })}:{' '}
                  {hasCustomPerformanceScope() ? 'Custom' : 'Global'}
                </Typography>
                <Typography className={classes.challengeDetails}>
                  {intl.formatMessage({ id: 'strings.visibility' })}: {hasCustomVisibilityScope() ? 'Custom' : 'Global'}
                </Typography>
                {challenge?.computedState === ChallengeComputedState.LIVE ? (
                  <Typography className={classes.challengeDetails}>{`Duration: ${new Date(
                    String(challenge?.schedule?.startAt),
                  ).toLocaleDateString()} -
                  ${new Date(String(challenge?.schedule?.endAt)).toLocaleDateString()},
                  ${Math.ceil(
                    (new Date(String(challenge?.schedule?.endAt)).getTime() - new Date().getTime()) /
                      (1000 * 60 * 60 * 24),
                    // ms to days
                  )} days remaining`}</Typography>
                ) : (
                  <Typography className={classes.challengeDetails}>
                    {`Duration: ${new Date(String(challenge?.schedule?.startAt)).toLocaleDateString()} -
                  ${new Date(String(challenge?.schedule?.endAt)).toLocaleDateString()}`}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>
        <Grid container sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Grid item>
            <Grid container gap={1}>
              <Button
                variant="contained"
                onClick={handleShowSettings}
                startIcon={<EditPencilIcon color="#fff" />}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '15px',
                  fontWeight: '500',
                  minWidth: '85px',
                  padding: '12px, 16px',
                  background: '#0BB163',
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                onClick={handleRecalculate}
                startIcon={<RecalculateIcon />}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '15px',
                  fontWeight: '500',
                  minWidth: '85px',
                  padding: '12px, 16px',
                  background: '#FFF',
                  color: '#404040',
                  border: 'solid 1px',
                  borderColor: '#D4D4D4',
                  '&:hover': {
                    background: '#F3F3F3',
                    borderColor: '#C4C4C4',
                  },
                }}
              >
                Recalculate
              </Button>
              <Button
                variant="contained"
                onClick={handleExportResults}
                startIcon={<ExportIcon />}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '15px',
                  fontWeight: '500',
                  minWidth: '85px',
                  padding: '12px, 16px',
                  background: '#FFF',
                  color: '#404040',
                  border: 'solid 1px',
                  borderColor: '#D4D4D4',
                  '&:hover': {
                    background: '#F3F3F3',
                    borderColor: '#C4C4C4',
                  },
                }}
              >
                Export results
              </Button>
              <Button
                variant="contained"
                target="_blank"
                rel="noreferrer"
                href={mixpanelUrl}
                startIcon={<Launch />}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '15px',
                  fontWeight: '500',
                  minWidth: '85px',
                  padding: '12px, 16px',
                  background: '#FFF',
                  color: '#404040',
                  border: 'solid 1px',
                  borderColor: '#D4D4D4',
                  '&:hover': {
                    background: '#F3F3F3',
                    borderColor: '#C4C4C4',
                  },
                }}
              >
                Mixpanel
              </Button>
              <IconButton
                size="medium"
                onClick={handleShowDeleteChallengeDialog}
                sx={{ border: 'solid 1px', borderRadius: '12px', borderColor: '#D4D4D4', minWidth: '40px' }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
          {challenge.computedState !== ChallengeComputedState.COMPLETED && (
            <Grid item>
              <Grid container gap={1}>
                <input
                  className={classes.textInput}
                  type="number"
                  onWheel={handleWheel}
                  placeholder="User-ID"
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value.trim())}
                />
                <Button
                  variant="contained"
                  onClick={handleAddPlayer}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '15px',
                    fontWeight: '500',
                    padding: '12px, 16px',
                    background: '#0BB163',
                  }}
                >
                  Add player
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid container sx={{ width: '100%', maxWidth: '100%', gap: 1.5 }}>
          <Grid item xs={12} sx={{ minWidth: 0 }}>
            <Typography
              style={{
                color: '#373D40',
                fontSize: '26px',
                fontWeight: '600',
              }}
            >
              Quick Insights
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
            {getQuickInsightChips(quickInsights)}
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ marginTop: '-4px' }}>
          <Grid container sx={{ alignItems: 'center', rowGap: 1 }}>
            <Grid item>
              <Typography
                style={{
                  color: '#373D40',
                  fontSize: '26px',
                  fontWeight: '600',
                }}
              >
                Leaderboard
              </Typography>
            </Grid>
            <Grid item xs display="flex" justifyContent="flex-end">
              <TextField
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '34px',
                    borderColor: '#D9D9D9',
                    fontSize: '16px',
                    minWidth: '240px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 12px',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', padding: '0 4px 4px', marginTop: '-2px' }}>
          <DataGrid
            autoHeight
            rows={filteredRows}
            columns={columns}
            loading={leaderboardStatus === 'loading'}
            rowSelection={false}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? classes.evenRow : '')}
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
              '& .MuiDataGrid-root, .MuiDataGrid-cell, .MuiDataGrid-columnHeader': {
                border: 'none',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cell': {
                maxWidth: '100%',
                whiteSpace: 'normal',
              },
              '& .MuiDataGrid-main': {
                overflowX: 'hidden',
              },
              '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
              '& .MuiDataGrid-topContainer': {
                borderTop: '0px solid var(--DataGrid-rowBorderColor)',
              },
            }}
          />
        </Grid>
      </Grid>
      {showDeleteChallengeDialog && (
        <DeleteChallengeDialog
          open={showDeleteChallengeDialog}
          onClose={() => setShowDeleteChallengeDialog(false)}
          onDelete={() => {
            handleDeleteChallenge()
          }}
        />
      )}
      {showRemovePlayerDialog && (
        <RemovePlayerDialog
          open={showRemovePlayerDialog}
          onClose={() => {
            setShowRemovePlayerDialog(false)
            setPlayerToRemove(null)
          }}
          onRemove={() => {
            handleConfirmRemovePlayer()
          }}
        />
      )}
    </>
  )
}

export default ChallengeLeaderboard
